import { create } from 'zustand';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type ThemeMode = 'dark' | 'light';

interface ThemeState {
  theme: ThemeMode;
  hydrated: boolean;
  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
}

// ---------------------------------------------------------------------------
// IndexedDB helper — lightweight wrapper, no external libraries
// ---------------------------------------------------------------------------
const DB_NAME = 'hms-settings';
const STORE_NAME = 'preferences';
const THEME_KEY = 'theme';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function readThemeFromDB(): Promise<ThemeMode | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(THEME_KEY);
      req.onsuccess = () => {
        const value = req.result as ThemeMode | undefined;
        resolve(value ?? null);
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    // IndexedDB unavailable — return null so the default is used
    return null;
  }
}

async function writeThemeToDB(theme: ThemeMode): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(theme, THEME_KEY);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // Silently fail — persistence is best-effort
  }
}

// ---------------------------------------------------------------------------
// Zustand Store
// ---------------------------------------------------------------------------
export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: 'dark',
  hydrated: false,

  toggleTheme: () => {
    const next: ThemeMode = get().theme === 'dark' ? 'light' : 'dark';
    set({ theme: next });
    writeThemeToDB(next);
  },

  setTheme: (theme: ThemeMode) => {
    set({ theme });
    writeThemeToDB(theme);
  },
}));

// ---------------------------------------------------------------------------
// Hydration — reads persisted theme once on module load (client only)
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  readThemeFromDB().then((persisted) => {
    if (persisted) {
      useThemeStore.setState({ theme: persisted, hydrated: true });
    } else {
      useThemeStore.setState({ hydrated: true });
    }
  });
}
